import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import {
  PartnerLoading,
  PartnerPageHeader,
  PartnerPageShell,
  partnerOxbloodBtnSx,
  partnerOutlineBtnSx,
} from '../components/PartnerListChrome';
import { DOCUMENT } from '../constants/documentTheme';

const PartnerClientDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('partnerToken')}` });

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/partners/crm/clients/${clientId}`, { headers: headers() });
        setClient(res.data.client);
        setNotes(res.data.client.notes || '');
      } catch (err) {
        setError(err.response?.data?.error || t('partnerCrm.loadFailed'));
      } finally {
        setLoading(false);
      }
    })();
  }, [clientId, t]);

  const saveNotes = async () => {
    setSaving(true);
    try {
      const res = await api.patch(
        `/partners/crm/clients/${clientId}`,
        { notes },
        { headers: headers() }
      );
      setClient(res.data.client);
    } catch (err) {
      setError(err.response?.data?.error || t('partnerCrm.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const planNext = async () => {
    try {
      const res = await api.post(`/partners/crm/clients/${clientId}/plan-next`, {}, { headers: headers() });
      const seed = encodeURIComponent(res.data.aiSeedPrompt || '');
      const email = encodeURIComponent(client.email || '');
      navigate(`/trips/create?clientEmail=${email}&aiSeed=${seed}`);
    } catch (err) {
      setError(err.response?.data?.error || t('partnerCrm.planNextFailed'));
    }
  };

  if (loading) {
    return (
      <PartnerPageShell>
        <PartnerLoading />
      </PartnerPageShell>
    );
  }

  if (!client) {
    return (
      <PartnerPageShell>
        <Alert severity="error">{error || t('partnerCrm.notFound')}</Alert>
      </PartnerPageShell>
    );
  }

  const prefs = client.preferences || {};

  return (
    <PartnerPageShell>
      <PartnerPageHeader
        title={client.display_name || client.username || client.email}
        subtitle={client.email}
        actions={
          <Stack direction="row" spacing={1}>
            <Button sx={partnerOutlineBtnSx} onClick={() => navigate('/partner/clients')}>
              {t('partnerCrm.back')}
            </Button>
            <Button sx={partnerOxbloodBtnSx} onClick={planNext}>
              {t('partnerCrm.planNext')}
            </Button>
          </Stack>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }}>{error}</Alert>}

      <Typography sx={{ mb: 1, color: DOCUMENT.ink3 }}>
        {t('partnerCrm.lifetimeValue')}:{' '}
        <strong>
          {client.lifetime_booking_value
            ? `$${Number(client.lifetime_booking_value).toLocaleString()}`
            : '—'}
        </strong>
      </Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {prefs.interests && <Chip label={prefs.interests} sx={{ borderRadius: 0 }} />}
        {prefs.hotelPreference && <Chip label={prefs.hotelPreference} sx={{ borderRadius: 0 }} />}
        {prefs.flightPreference && <Chip label={prefs.flightPreference} sx={{ borderRadius: 0 }} />}
        {prefs.budget && <Chip label={String(prefs.budget)} sx={{ borderRadius: 0 }} />}
      </Stack>

      <TextField
        fullWidth
        multiline
        minRows={3}
        label={t('partnerCrm.notes')}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        sx={{ mb: 1 }}
      />
      <Button onClick={saveNotes} disabled={saving} sx={{ ...partnerOutlineBtnSx, mb: 3 }}>
        {t('partnerCrm.saveNotes')}
      </Button>

      <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: '1.25rem', mb: 1 }}>
        {t('partnerCrm.trips')}
      </Typography>
      <Stack spacing={1}>
        {(client.trips || []).map((trip) => (
          <Box
            key={trip.id}
            onClick={() => navigate(`/partner/trips/${trip.id}`)}
            sx={{
              py: 1.25,
              borderBottom: `1px solid ${DOCUMENT.rule}`,
              cursor: 'pointer',
              '&:hover': { bgcolor: DOCUMENT.ivory },
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>{trip.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {trip.pipeline_stage || 'planning'}
              {trip.start_date ? ` · ${String(trip.start_date).slice(0, 10)}` : ''}
              {trip.budget ? ` · $${trip.budget}` : ''}
            </Typography>
          </Box>
        ))}
      </Stack>
    </PartnerPageShell>
  );
};

export default PartnerClientDetail;
